import { useEffect, useState } from 'react';
import { Constants, Globals } from 'utils';
import { AuthService, BankService, UserService } from 'services';
import { ContactCallIcon, ContactMessageIcon, ContactVideoCallIcon } from "assets/icons";

type TForm = {
  from_web: number
  level_id?: number
  affiliate_type?: number
  name: string
  lastname: string
  birthdate: string
  email: string
  document: string
  address: string
  document_type_id: any
  phonecode_id: any
  phone: string
  state_id: string
  city_id: string
  photo: any
  photo_blob: string
  description: string
  seller_code: string
  password: string
  password_confirmation: string
  // Datos bancarios
  bank_account: {
    bank_id: number | string
    number: string
    phone_code_id: number | string
    phone: string
    document_type_id: number | string
    document: string
  };
  // Datos de persona de contacto
  contact_person_name: string
  contact_person_email: string
  contact_person_doc_type_id: string
  contact_person_document: string
  contact_person_phone_code_id: string
  contact_person_phone: string
  // Datos profesionales
  medical_number: string
  university_title: any
  medical_carnet: any
  insai_file: any
  mpps_file: any
  speciality_id: string
  speciality_name: string
  specialties: any[]
  language_id: string
  languages: string[]
  years_experience: string
  // Áreas de especialización
  areas_specialization: string[]
  animal_id: string
  animal_name: string
  animals_affiliate: any[]
  service_id: string
  service_name: string
  services: any[]
  category_id: string
  category_name: string
  categories: any[]
  vet_name: string
  veterinarians: any[]
  // Horario de atención
  consultation: {
    init_hour: string
    end_hour: string
    days: string[]
    teleconsultation_rate_id: string
    home_service: string
    emergency_service: string
    delivery_service: string
    contact_methods: string[]
  };
  // Productos
  products: {
    name: string
    image: any
    image_blob: string
  }[];
  terms: number
  terms_truth: number
  terms_verify: number
  terms_law: number
}

type TListItem = { value: string, label: string, icon?: any }

type TSelects = {
  specialties: TListItem[]
  languages: TListItem[]
  categories: TListItem[]
  states: TListItem[]
  cities: TListItem[];
  banks: TListItem[];
  teleconsultationRates: TListItem[]
  animals: TListItem[];
  services: TListItem[];
  areas_specialization: TListItem[];
  weekDays: TListItem[];
  contact_methods: TListItem[];
  document_types: TListItem[];
  phonecodes: TListItem[];
}

export const useBasics = (affiliate_type: number) => {
  const [step, setStep] = useState<number>(1);
  const [modal, setModal] = useState<number>(0);
  const [selects, setSelects] = useState<TSelects>({
    specialties: [],
    languages: [],
    categories: [],
    states: [],
    cities: [],
    banks: [],
    teleconsultationRates: [],
    animals: [],
    services: [],
    areas_specialization: [],
    weekDays: [],
    contact_methods: [],
    document_types: [],
    phonecodes: [],
  });

  const [form, setForm] = useState<TForm>({
    from_web: 1,
    level_id: Constants.LEVELS.AFFILIATE,
    affiliate_type,
    name: '',
    lastname: '',
    birthdate: '',
    email: '',
    document: '',
    address: '',
    document_type_id: '',
    phonecode_id: '',
    phone: '',
    state_id: '',
    city_id: '',
    photo: '',
    photo_blob: '',
    description: '',
    seller_code: '',
    password: '',
    password_confirmation: '',
    // Datos bancarios
    bank_account: {
      bank_id: '',
      number: '',
      phone_code_id: '',
      phone: '',
      document_type_id: '',
      document: '',
    },
    // Datos de persona de contacto
    contact_person_name: '',
    contact_person_email: '',
    contact_person_doc_type_id: '',
    contact_person_document: '',
    contact_person_phone_code_id: '',
    contact_person_phone: '',
    // Datos profesionales
    university_title: null,
    medical_carnet: null,
    insai_file: null,
    mpps_file: null,
    speciality_id: '',
    speciality_name: '',
    specialties: [],
    language_id: '',
    languages: [],
    years_experience: '',
    medical_number: '',
    // Áreas de especialización
    areas_specialization: [],
    animal_id: '',
    animal_name: '',
    animals_affiliate: [],
    service_id: '',
    service_name: '',
    services: [],
    category_id: '',
    category_name: '',
    categories: [],
    vet_name: '',
    veterinarians: [],
    // Horario de atención
    consultation: {
      init_hour: '',
      end_hour: '',
      days: [],
      teleconsultation_rate_id: '',
      home_service: '',
      emergency_service: '',
      delivery_service: '',
      contact_methods: [],
    },
    products: [],
    terms: 0,
    terms_truth: 0,
    terms_verify: 0,
    terms_law: 0,
  });

  const getSelects = async () => {
    try {
      const res: any = await UserService.admin.getSelects();
      const banks: any = await UserService.admin.getBanks();
      const resDocs: any = await BankService.admin.getData();

      const toSelect = (values?: any[]) => {
        return (values ?? [])?.map((i: any) => ({ value: String(i.id), label: i.name })) || [];
      }

      setSelects({
        specialties: res.specialties?.map((i: any) => ({ value: i.name, label: i.name })) || [],
        languages: toSelect(res.languages)?.sort((a: any, b: any) => a.label.localeCompare(b.label)) || [],
        categories: toSelect(res.categories),
        states: toSelect(res.states) || [],
        cities: [],
        banks: banks.banks?.map((i: any) => ({ value: String(i.id), label: i.name })) || [],
        teleconsultationRates: res.teleconsultationRates?.map((i: any) => ({ value: String(i.id), label: Globals.formatMiles(i.amount, true, '$') })) || [],
        animals: Object.values(Constants.AFFILIATE_ANIMALS).map(item => ({ value: String(item.id), label: item.name })),
        services: Object.values(Constants.SERVICES).map(item => ({ value: String(item.id), label: item.name })),
        areas_specialization: [
          { value: Constants.USER.AREAS_SPECIALIZATION.PEQ_ANIMALES.id.toString(), label: Constants.USER.AREAS_SPECIALIZATION.PEQ_ANIMALES.name },
          { value: Constants.USER.AREAS_SPECIALIZATION.GRA_ANIMALES.id.toString(), label: Constants.USER.AREAS_SPECIALIZATION.GRA_ANIMALES.name },
          { value: Constants.USER.AREAS_SPECIALIZATION.EXOTICOS.id.toString(), label: Constants.USER.AREAS_SPECIALIZATION.EXOTICOS.name },
        ],
        weekDays: [
          { value: '1', label: 'Lunes' },
          { value: '2', label: 'Martes' },
          { value: '3', label: 'Miércoles' },
          { value: '4', label: 'Jueves' },
          { value: '5', label: 'Viernes' },
          { value: '6', label: 'Sábado' },
          { value: '7', label: 'Domingo' },
        ],
        contact_methods: [
          { value: '1', icon: ContactVideoCallIcon, label: 'Video llamada' },
          { value: '2', icon: ContactCallIcon, label: 'Llamada telefónica' },
          { value: '3', icon: ContactMessageIcon, label: 'Mensaje de texto' },
        ],
        phonecodes: toSelect(resDocs.phonecodes) || [],
        document_types: toSelect(resDocs.document_types) || [],
      });

    } catch (error) {
      console.log(error)
    }
  }

  const getCities = async () => {
    try {
      const state = await Globals.getUpdatedState(setForm);
      const res: any = await AuthService.getCities({ state_id: state.state_id });
      setSelects(s => ({ ...s, cities: res?.cities?.map((i: any) => ({ value: String(i.id), label: i.name })) || [] }));

    } catch (error) {
      console.log(error);
    }
  }

  const onChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const target = e.target ?? e.currentTarget;
    setForm(s => ({ ...s, [target.name]: target.value }));
  }

  const onChangeValue = <T extends keyof TForm>(target: T, value: any) => {
    setForm(s => ({ ...s, [target]: value }));
  }

  useEffect(() => {
    getSelects();
  }, []);

  return {
    form,
    getCities,
    modal,
    onChange,
    onChangeValue,
    selects,
    setModal,
    setStep,
    step,
  }
}